import React from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import StoreIcon from "@mui/icons-material/Store";
import CategoryIcon from "@mui/icons-material/Category";
import InventoryIcon from "@mui/icons-material/Inventory";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PaymentIcon from "@mui/icons-material/Payment";
import PeopleIcon from "@mui/icons-material/People";
import SmsIcon from "@mui/icons-material/Sms";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import HandshakeIcon from "@mui/icons-material/Handshake";
import ArticleIcon from "@mui/icons-material/Article";
import BarChartIcon from "@mui/icons-material/BarChart";
import TerminalIcon from "@mui/icons-material/Terminal";

const localUser = JSON.parse(localStorage.getItem("kdmUser"));
const superUser = localUser?.sub === process.env.REACT_APP_ADMIN_UID;

const DRAWER_WIDTH = 260;

const menuSections = [
  {
    title: "Principal",
    items: [
      { text: "Dashboard", icon: DashboardIcon, path: "/" },
      { text: "Points de vente", icon: StoreIcon, path: "/merchants" },
    ],
  },
  {
    title: "Catalogue",
    items: [
      { text: "Mes menus", icon: CategoryIcon, path: "/categories" },
      { text: "Mes articles", icon: InventoryIcon, path: "/items" },
    ],
  },
  {
    title: "Commerce",
    items: [
      { text: "Mes ventes", icon: MonetizationOnIcon, path: "/sales" },
      { text: "Stats Ventes", icon: BarChartIcon, path: "/sales-stats" },
      { text: "Paiements", icon: PaymentIcon, path: "/payments" },
      { text: "Mes clients", icon: PeopleIcon, path: "/customers" },
    ],
  },
  {
    title: "Marketing",
    items: [
      { text: "Campagnes SMS", icon: SmsIcon, path: "/sms" },
      { text: "Mes partenaires", icon: HandshakeIcon, path: "/partners" },
      { text: "Articles de blog", icon: ArticleIcon, path: "/articles" },
      {
        text: "Tickets générés",
        icon: ConfirmationNumberIcon,
        path: "/tickets",
      },
      { text: "Formulaires", icon: AssignmentIcon, path: "/formalyze" },
      { text: "Stats Formulaires", icon: BarChartIcon, path: "/form-stats" },
    ],
  },
  {
    title: "Administration",
    items: [
      { text: "Stats Clients", icon: BarChartIcon, path: "/customers-stats" },
      { text: "Logs Serveur", icon: TerminalIcon, path: "/logs" },
    ],
  },
];

if (superUser) {
  // Ajouter "Utilisateurs" dans la section Administration
  const adminSection = menuSections.find(
    (section) => section.title === "Administration"
  );
  if (adminSection) {
    adminSection.items.unshift({
      text: "Utilisateurs",
      icon: SupervisorAccountIcon,
      path: "/users",
    });
  }
}

export default function ModernSidebar() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Ne pas afficher le sidebar sur mobile (le MobileDrawer le remplace)
  if (isMobile) {
    return null;
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          backgroundColor: "background.paper",
          mt: "64px",
          height: "calc(100vh - 64px)",
        },
      }}
    >
      <Box sx={{ overflow: "auto", px: 2, py: 3 }}>
        {menuSections.map((section, sectionIndex) => (
          <Box key={sectionIndex} sx={{ mb: 3 }}>
            <Typography
              variant="overline"
              sx={{
                px: 2,
                py: 1,
                color: "text.secondary",
                fontWeight: 600,
                fontSize: "0.6875rem",
                letterSpacing: "0.08em",
              }}
            >
              {section.title}
            </Typography>
            <List sx={{ pt: 0.5 }}>
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <ListItemButton
                    key={item.path}
                    component={NavLink}
                    to={item.path}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      py: 1.25,
                      px: 2,
                      color: isActive ? "primary.main" : "text.secondary",
                      backgroundColor: isActive
                        ? "action.selected"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "action.selected"
                          : "action.hover",
                      },
                      "& .MuiListItemIcon-root": {
                        color: isActive ? "primary.main" : "text.secondary",
                        minWidth: 40,
                      },
                      "& .MuiListItemText-primary": {
                        fontWeight: isActive ? 600 : 500,
                        fontSize: "0.9375rem",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                );
              })}
            </List>
            {sectionIndex < menuSections.length - 1 && (
              <Divider sx={{ my: 2 }} />
            )}
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}
